import axios from "axios";
import apiUrls from "src/api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IAppLoaderAction } from "src/common/state/loaderHandleMiddleware";
import { UserFormResults } from "../components/chatState";
import { IGptConversation } from "src/model/model";
import { RootState } from "src/common/state/store";
import { IMedia } from "src/model/unityObject";
import { getMedia } from "src/features/gallery/state/gallerySlice";
import { syncUserWithServer } from "src/features/profile/state/profileSlice";

const GPT_CONVERSATIONS_PAGE_SIZE = 20;

interface IGptConversationsPagination {
  total: number;
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface IWixarAIStore {
  activeGptConversation?: IGptConversation | null;
  gptConversations: IGptConversation[];
  gptConversationsPagination: IGptConversationsPagination;
  idOfLatestProjectGroupCursedByAi: number | null;
  scenarioHelperContextPdfs: IMedia[];
  scenarioHelperRagAgentIds: number[];
  chosenLlmProvider: string;
  lastAiModuleConversationId: number | null;
  lastAiModuleOutputIndex: number | null;
  lastAiModuleGenerateBlockadeImages: boolean;
}

const initialPagination: IGptConversationsPagination = {
  total: 0,
  hasMore: true,
  isLoadingMore: false,
};

const initialState: IWixarAIStore = {
  activeGptConversation: null,
  gptConversations: [],
  gptConversationsPagination: initialPagination,
  idOfLatestProjectGroupCursedByAi: null,
  scenarioHelperContextPdfs: [],
  scenarioHelperRagAgentIds: [],
  chosenLlmProvider: "openai",
  lastAiModuleConversationId: null,
  lastAiModuleOutputIndex: null,
  lastAiModuleGenerateBlockadeImages: false,
};

export const fetchGptConversations = createAsyncThunk(
  "gpt/fetchGptConversations",
  async ({
    start = 0,
    limit = GPT_CONVERSATIONS_PAGE_SIZE,
  }: { start?: number; limit?: number } & IAppLoaderAction) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(
      `${apiUrls.gpt.findAllWithNestedStuff}?start=${start}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body = await response.json();
    return { data: body.data, total: body.total ?? 0 };
  },
);

export const fetchMoreGptConversations = createAsyncThunk(
  "gpt/fetchMoreGptConversations",
  async ({ start, limit = GPT_CONVERSATIONS_PAGE_SIZE }: { start: number; limit?: number }) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(
      `${apiUrls.gpt.findAllWithNestedStuff}?start=${start}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body = await response.json();
    return { data: body.data, total: body.total ?? 0 };
  },
);

export const createChat = createAsyncThunk(
  "gpt/createChat",
  async (
    {
      userPrompt,
      userFormResults,
      sourceMediasWithContext = undefined,
      sourceMedias,
      chosenLlmProvider,
      ragAgentIds = [],
      cb,
      cbOnRequestEnd,
    }: {
      userPrompt: string;
      userFormResults: UserFormResults;
      sourceMediasWithContext?: number[];
      sourceMedias;
      chosenLlmProvider: string;
      ragAgentIds?: number[];
      cb?: (chunk: string) => void;
      cbOnRequestEnd?: () => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.createChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
            userFormResults,
            sourceMediasWithContext,
            service: chosenLlmProvider,
            rag_agent_ids: ragAgentIds,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";
      let metaReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          // \x1E (Record Separator) delimits GPT text from backend metadata.
          // Don't forward the metadata portion to the streaming display callback.
          if (!metaReceived) {
            const sepIdx = chunk.indexOf("\x1E");
            if (sepIdx !== -1) {
              metaReceived = true;
              if (cb && sepIdx > 0) cb(chunk.slice(0, sepIdx));
            } else if (cb) {
              cb(chunk);
            }
          }
        }
      }

      // Extract conversation ID from backend metadata appended after \x1E
      let conversationId: number | undefined;
      const sepIdx = botMessage.indexOf("\x1E");
      if (sepIdx !== -1) {
        try {
          const meta = JSON.parse(botMessage.slice(sepIdx + 1));
          conversationId = meta.conversationId;
        } catch {
          /* metadata parse failed — continue without ID */
        }
        botMessage = botMessage.slice(0, sepIdx);
      }

      const timestamp = new Date().toISOString();

      const newConversation: IGptConversation = {
        id: conversationId,
        conversation_metadata: {
          model: "gpt-4",
          timestamp,
          userPrompts: [userPrompt],
          userFormResults: userFormResults,
          source_media_with_context: [],
        },
        conversation_data: {
          output: [botMessage],
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        source_media_with_context: sourceMedias.filter((sm) =>
          sourceMediasWithContext.includes(Number(sm.id)),
        ),
      };

      if (cbOnRequestEnd) {
        cbOnRequestEnd();
      }

      return newConversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (createChat)");
    }
  },
);

export const updateChat = createAsyncThunk(
  "gpt/updateChat",
  async (
    {
      userPrompt,
      conversationId,
      ragAgentIds = [],
      cb,
    }: {
      userPrompt: string;
      conversationId: number;
      ragAgentIds?: number[];
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.updateChat, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ userPrompt, conversationId, rag_agent_ids: ragAgentIds }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return { userPrompt, botResponse: botMessage };
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateChat)");
    }
  },
);

export const resetChat = createAsyncThunk(
  "gpt/resetChat",
  async ({ cb }: { cb(): any } & IAppLoaderAction) => {
    return await axios
      .put(apiUrls.gpt.resetChat)
      .then((res) => {
        cb();
        return res;
      })
      .catch((e) => {
        console.error("resetChat did an oopsie: ", e);
        return e;
      });
  },
);

export const askWixarpedia = createAsyncThunk(
  "gpt/askWixarpedia",
  async (
    {
      userPrompt,
      cb,
    }: {
      userPrompt: string;
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.askWixarpedia, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return botMessage;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (askWixarpedia)");
    }
  },
);

export const updateGptConversationName = createAsyncThunk(
  "gpt/updateName",
  async (
    {
      id,
      newName,
      cb,
    }: {
      id: number;
      newName: string;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      return await axios
        .put(`${apiUrls.gpt.gptConversations}/${id}`, {
          name: newName,
        })
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts :", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateGptConversationName)");
    }
  },
);

export const deleteGptConversation = createAsyncThunk(
  "gpt/deleteName",
  async (
    {
      id,
      cb,
    }: {
      id: number;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      return await axios
        .delete(`${apiUrls.gpt.gptConversations}/${id}`)
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts 299:", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (deleteGptConversation)");
    }
  },
);

export const generateProjectGroupFromAiGraph = createAsyncThunk(
  "gpt/generateProjectGroupFromAiGraph",
  async (
    {
      conversationId,
      name,
      generateBlockadeImages = false,
      relatedGptOutputIndex = undefined,
      cb,
    }: {
      conversationId: number;
      name: string;
      generateBlockadeImages?: boolean;
      relatedGptOutputIndex?: number;
      cb?: () => void;
    } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const jwt = localStorage.getItem("wixar.auth.jwt");
      if (!jwt) {
        throw new Error("JWT token not found in localStorage.");
      }

      const response = await fetch(`${apiUrls.gpt.generateTemplate}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name,
          conversationId,
          generateBlockadeImages,
          relatedGptOutputIndex,
          createAsProjectGroup: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the API");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Readable stream is not supported");
      }

      const decoder = new TextDecoder();
      let result = "";

      const readNextChunk = async () => {
        const { done, value } = await reader.read();

        if (done) return; // End recursion when the stream is done

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          chunk
            .trim()
            .split("\n")
            .forEach((line) => {
              if (!line) return;

              try {
                const parsed = JSON.parse(line);
                if (parsed.status === "complete") {
                  result = parsed.data;
                } else if (parsed.error) {
                  throw new Error(parsed.error);
                }
              } catch (error) {
                console.error("Error parsing chunk:", error, "Raw chunk:", line);
              }
            });
        }

        // Recursively read the next chunk
        await readNextChunk();
      };

      await readNextChunk(); // Start the recursive read

      if (cb) cb();

      return result; // Return final result
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (generateProjectGroupFromAiGraph)");
    }
  },
);

export const fetchAndUpdateSpecificConversation = createAsyncThunk(
  "gpt/fetchAndUpdateSpecificConversation",
  async ({ conversationId }: { conversationId: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrls.gpt.gptConversations}/${conversationId}`);

      if (!response.data) {
        throw new Error("No conversation data returned");
      }

      return response.data;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (fetchAndUpdateSpecificConversation)");
    }
  },
);

export const chatbotSlice = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    setActiveGptConversation: (state: IWixarAIStore, action) => {
      state.activeGptConversation = action.payload;
      state.chosenLlmProvider = action.payload?.conversation_metadata?.model.includes("mistral")
        ? "mistral"
        : "openai";
    },
    resetActiveGptConversation: (state: IWixarAIStore) => {
      state.activeGptConversation = null;
    },
    setIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore, action) => {
      state.idOfLatestProjectGroupCursedByAi = action.payload;
    },
    resetIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore) => {
      state.idOfLatestProjectGroupCursedByAi = null;
    },
    addScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs.push(action.payload);
    },
    removeScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.filter(
        (pdf) => pdf.id !== action.payload,
      );
    },
    setScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs = action.payload;
    },
    resetScenarioHelperContextPdfs: (state: IWixarAIStore) => {
      state.scenarioHelperContextPdfs = [];
    },
    setScenarioHelperRagAgentIds: (state: IWixarAIStore, action) => {
      state.scenarioHelperRagAgentIds = action.payload;
    },
    resetScenarioHelperRagAgentIds: (state: IWixarAIStore) => {
      state.scenarioHelperRagAgentIds = [];
    },
    setChosenLlmProvider: (state: IWixarAIStore, action) => {
      state.chosenLlmProvider = action.payload;
    },
    setLastAiModuleContext: (
      state: IWixarAIStore,
      action: {
        payload: {
          conversationId: number;
          relatedGptOutputIndex: number;
          generateBlockadeImages: boolean;
        };
      },
    ) => {
      state.lastAiModuleConversationId = action.payload.conversationId;
      state.lastAiModuleOutputIndex = action.payload.relatedGptOutputIndex;
      state.lastAiModuleGenerateBlockadeImages = action.payload.generateBlockadeImages;
    },
    resetLastAiModuleContext: (state: IWixarAIStore) => {
      state.lastAiModuleConversationId = null;
      state.lastAiModuleOutputIndex = null;
      state.lastAiModuleGenerateBlockadeImages = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGptConversations.fulfilled, (state, { payload }) => {
      state.gptConversations = payload.data;
      const loaded = Array.isArray(payload.data) ? payload.data.length : 0;
      state.gptConversationsPagination = {
        total: payload.total,
        hasMore: loaded < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchMoreGptConversations.pending, (state) => {
      state.gptConversationsPagination.isLoadingMore = true;
    });
    builder.addCase(fetchMoreGptConversations.fulfilled, (state, { payload }) => {
      const existingIds = new Set(state.gptConversations.map((c) => c.id));
      const newConversations = (payload.data || []).filter(
        (c: IGptConversation) => !existingIds.has(c.id),
      );
      state.gptConversations.push(...newConversations);
      state.gptConversationsPagination = {
        total: payload.total,
        hasMore: state.gptConversations.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchMoreGptConversations.rejected, (state) => {
      state.gptConversationsPagination.isLoadingMore = false;
    });
    builder.addCase(createChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.push(payload);
      state.gptConversationsPagination.total += 1;
    });
    builder.addCase(updateChat.fulfilled, (state, { payload }) => {
      if (state.activeGptConversation) {
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.userPrompt);
        state.activeGptConversation.conversation_data.output.push(payload.botResponse);

        // Sync updated conversation back into the list so switching away/back loads fresh data
        const idx = state.gptConversations.findIndex(
          (c) => c.id === state.activeGptConversation?.id,
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            ...state.activeGptConversation,
          };
        }
      }
    });
    builder.addCase(resetChat.fulfilled, (state) => {
      state.activeGptConversation = null;
      state.gptConversations = [];
      state.gptConversationsPagination = { ...initialPagination };
    });
    builder.addCase(updateGptConversationName.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return payload;
        }
        return conversation;
      });
      state.gptConversations = updatedGptConversationArray;
    });
    builder.addCase(deleteGptConversation.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].filter((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return false;
        }
        return true;
      });
      state.gptConversations = updatedGptConversationArray;
      state.gptConversationsPagination.total = Math.max(
        0,
        state.gptConversationsPagination.total - 1,
      );
      if (Number(state.activeGptConversation?.id) === Number(payload?.id)) {
        state.activeGptConversation = null;
      }
    });
    builder.addCase(fetchAndUpdateSpecificConversation.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload.id)) {
          return payload;
        }
        return conversation;
      });

      state.gptConversations = updatedGptConversationArray;

      if (state.activeGptConversation?.id === payload.id) {
        state.activeGptConversation = payload;
      }
    });
    builder.addCase(getMedia.fulfilled, (state, { payload }) => {
      const updatedMedia = payload;
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.map((pdf) => {
        return Number(pdf.id) === Number(updatedMedia.id) ? updatedMedia : pdf;
      });
    });
    builder.addCase(syncUserWithServer.fulfilled, (state, { payload }) => {
      const updatedGptConversation = payload.active_gpt_conversation;
      state.activeGptConversation = updatedGptConversation;
      state.scenarioHelperContextPdfs = updatedGptConversation?.source_media_with_context ?? [];

      // Keep the conversations list in sync
      if (updatedGptConversation?.id) {
        const idx = state.gptConversations.findIndex((c) => c.id === updatedGptConversation.id);
        if (idx !== -1) {
          state.gptConversations[idx] = updatedGptConversation;
        }
      }
    });
  },
});

export const chatbotReducer = chatbotSlice.reducer;

export const getActiveGptConversation = (state: RootState) => state.chatbot.activeGptConversation;
export const getIdOfLatestProjectGroupCursedByAi = (state: RootState) =>
  state.chatbot.idOfLatestProjectGroupCursedByAi;
export const getScenarioHelperContextPdfs = (state: RootState) =>
  state.chatbot.scenarioHelperContextPdfs;
export const getScenarioHelperRagAgentIds = (state: RootState) =>
  state.chatbot.scenarioHelperRagAgentIds;
export const getChosenLlmProvider = (state: RootState) => state.chatbot.chosenLlmProvider;
export const getLastAiModuleContext = (state: RootState) => ({
  conversationId: state.chatbot.lastAiModuleConversationId,
  outputIndex: state.chatbot.lastAiModuleOutputIndex,
  generateBlockadeImages: state.chatbot.lastAiModuleGenerateBlockadeImages,
});

export const getGptConversationsPagination = (state: RootState) =>
  state.chatbot.gptConversationsPagination;

export const getGptConversationsSortedByRecency = (state: RootState) => {
  const sortedConversations = [...state.chatbot.gptConversations].sort((a, b) => {
    const timestampA = new Date(a.updatedAt).getTime();
    const timestampB = new Date(b.updatedAt).getTime();
    return timestampB - timestampA;
  });

  return sortedConversations;
};

export const {
  setActiveGptConversation,
  resetActiveGptConversation,
  setIdOfLatestProjectGroupCursedByAi,
  addScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  resetScenarioHelperContextPdfs,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
  setChosenLlmProvider,
  setLastAiModuleContext,
  resetLastAiModuleContext,
} = chatbotSlice.actions;
